import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { EnemySpawner } from '../systems/EnemySpawner';
import { WeaponSystem } from '../systems/WeaponSystem';
import { YarnBallWeapon } from '../systems/YarnBallWeapon';
import { LaserPointerWeapon } from '../systems/LaserPointerWeapon';
import { FishWeapon } from '../systems/FishWeapon';
import { CatnipBombWeapon } from '../systems/CatnipBombWeapon';
import { CatCollarWeapon } from '../systems/CatCollarWeapon';
import { MilkSplashWeapon } from '../systems/MilkSplashWeapon';
import { ScratchingPostWeapon } from '../systems/ScratchingPostWeapon';
import { UpgradeSystem } from '../systems/UpgradeSystem';
import { UpgradeUI } from './UpgradeUI';
import { PowerUpDisplay } from '../ui/PowerUpDisplay';
import { SoundManager } from '../systems/SoundManager';
import { ExperienceGem } from '../entities/ExperienceGem';
import { HealthPickup } from '../entities/HealthPickup';
import { PowerUp, PowerUpType } from '../entities/PowerUp';
import { GameConfig } from '../config/GameConfig';
import { MetaProgression } from '../systems/MetaProgression';
import { Achievements } from '../systems/Achievements';

export class GameScene extends Phaser.Scene {
  private player!: Player;
  private enemySpawner!: EnemySpawner;
  private weaponSystem!: WeaponSystem;
  private metaProgression!: MetaProgression;
  private achievements!: Achievements;
  private baseDamageMultiplier: number = 1;
  private xpGainMultiplier: number = 1;
  private baseMagnetRange: number = GameConfig.PLAYER.BASE_MAGNET_RANGE;
private cooldownMultiplier: number = 1;
private weaponsEquipped: Set<string> = new Set();
private powerUpsCollected: number = 0;
private xpGemsCollected: number = 0;
private yarnBallWeapon?: YarnBallWeapon;
private laserPointerWeapon?: LaserPointerWeapon;
private fishWeapon?: FishWeapon;
private catnipBombWeapon?: CatnipBombWeapon;
private catCollarWeapon?: CatCollarWeapon;
private milkSplashWeapon?: MilkSplashWeapon;
private scratchingPostWeapon?: ScratchingPostWeapon;
private upgradeSystem!: UpgradeSystem;
private upgradeUI!: UpgradeUI;
private powerUpDisplay!: PowerUpDisplay;
private soundManager!: SoundManager;
private experienceGems!: Phaser.Physics.Arcade.Group;
private healthPickups!: Phaser.Physics.Arcade.Group;
private powerUps!: Phaser.Physics.Arcade.Group;
private gameTime: number = 0;
private enemiesKilled: number = 0;
private timeText!: Phaser.GameObjects.Text;
private healthText!: Phaser.GameObjects.Text;
private levelText!: Phaser.GameObjects.Text;

constructor() {
super({ key: 'GameScene' });
}

create(): void {
this.initWorld();
this.initPlayer();
this.initSystems();
this.initEventListeners();
this.initGroups();
this.initPhysics();
this.initUI();
this.initGameTimer();
}

private initWorld(): void {
// Setup camera bounds (large world for exploration)
this.cameras.main.setBounds(-GameConfig.WORLD.BOUNDS / 2, -GameConfig.WORLD.BOUNDS / 2, GameConfig.WORLD.BOUNDS, GameConfig.WORLD.BOUNDS);
this.physics.world.setBounds(-GameConfig.WORLD.BOUNDS / 2, -GameConfig.WORLD.BOUNDS / 2, GameConfig.WORLD.BOUNDS, GameConfig.WORLD.BOUNDS);

// Create simple ground
this.createBackground();
}

private initPlayer(): void {
// Create player at center
this.player = new Player(this, 0, 0);

// Load and apply meta progression upgrades
this.metaProgression = new MetaProgression();
this.applyMetaUpgrades();

// Initialize achievements system
this.achievements = new Achievements(this);

// Camera follows player
this.cameras.main.startFollow(this.player, true, 0.1, 0.1);
}

private initSystems(): void {
// Create systems
this.enemySpawner = new EnemySpawner(this, this.player);
this.weaponSystem = new WeaponSystem(this, this.player, this.enemySpawner.getEnemies());
this.upgradeSystem = new UpgradeSystem(this, this.player, this.weaponSystem);
this.upgradeUI = new UpgradeUI(this);
this.powerUpDisplay = new PowerUpDisplay(this);
this.soundManager = new SoundManager(this);

// Apply base damage multiplier from meta upgrades to weapon system
if (this.baseDamageMultiplier > 1) {
this.weaponSystem.updateDamageMultiplier(this.baseDamageMultiplier);
}
}

private initEventListeners(): void {
// Listen for player level up
this.events.on('player-levelup', () => {
this.soundManager.playLevelUp();
this.handleLevelUp();
});

// Listen for pickups
this.events.on('powerup-collected', () => {
this.soundManager.playPowerUpCollected();
this.powerUpsCollected++;

// Check power hungry achievement
if (this.powerUpsCollected >= 10) {
this.achievements.unlock('power_hungry');
}
});

this.events.on('xp-collected', () => {
this.soundManager.playXPCollected();
this.xpGemsCollected++;

// Check collector achievement
const totalGems = this.metaProgression.getStats().totalKills + this.xpGemsCollected; // Approximation
if (totalGems >= 1000) {
this.achievements.unlock('collector');
}
});

this.events.on('health-collected', () => {
this.soundManager.playHealthPickup();
});

// Listen for power-up effects
this.events.on('damage-boost-start', (multiplier: number) => {
// Apply power-up multiplier on top of base damage multiplier
const totalMultiplier = this.baseDamageMultiplier * multiplier;
if (this.weaponSystem) this.weaponSystem.updateDamageMultiplier(totalMultiplier);
if (this.yarnBallWeapon) this.yarnBallWeapon.updateDamageMultiplier(totalMultiplier);
if (this.laserPointerWeapon) this.laserPointerWeapon.updateDamageMultiplier(totalMultiplier);
if (this.fishWeapon) this.fishWeapon.updateDamageMultiplier(totalMultiplier);
if (this.catnipBombWeapon) this.catnipBombWeapon.updateDamageMultiplier(totalMultiplier);
if (this.catCollarWeapon) this.catCollarWeapon.updateDamageMultiplier(totalMultiplier);
if (this.milkSplashWeapon) this.milkSplashWeapon.updateDamageMultiplier(totalMultiplier);
if (this.scratchingPostWeapon) this.scratchingPostWeapon.updateDamageMultiplier(totalMultiplier);
});

this.events.on('damage-boost-end', () => {
// Reset to base damage multiplier from meta upgrades
if (this.weaponSystem) this.weaponSystem.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.yarnBallWeapon) this.yarnBallWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.laserPointerWeapon) this.laserPointerWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.fishWeapon) this.fishWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.catnipBombWeapon) this.catnipBombWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.catCollarWeapon) this.catCollarWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.milkSplashWeapon) this.milkSplashWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
if (this.scratchingPostWeapon) this.scratchingPostWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
});

this.events.on('magnet-boost-start', (range: number) => {
// Update magnet range for all pickups
this.experienceGems.getChildren().forEach((gem: any) => {
if (gem.setMagnetRange) gem.setMagnetRange(range);
});
this.healthPickups.getChildren().forEach((pickup: any) => {
if (pickup.setMagnetRange) pickup.setMagnetRange(range);
});
this.powerUps.getChildren().forEach((powerUp: any) => {
if (powerUp.setMagnetRange) powerUp.setMagnetRange(range);
});
});

this.events.on('magnet-boost-end', () => {
// Reset magnet range to base value from meta upgrades
this.experienceGems.getChildren().forEach((gem: any) => {
if (gem.setMagnetRange) gem.setMagnetRange(this.baseMagnetRange);
});
this.healthPickups.getChildren().forEach((pickup: any) => {
if (pickup.setMagnetRange) pickup.setMagnetRange(this.baseMagnetRange);
});
this.powerUps.getChildren().forEach((powerUp: any) => {
if (powerUp.setMagnetRange) powerUp.setMagnetRange(this.baseMagnetRange);
});
});

// Listen for enemy deaths to spawn gems and pickups
this.events.on('enemy-killed', (x: number, y: number, isBoss?: boolean) => {
this.spawnExperienceGem(x, y);

// Limit total pickups for performance
const totalPickups = this.experienceGems.getChildren().length +
this.healthPickups.getChildren().length +
this.powerUps.getChildren().length;

if (totalPickups < GameConfig.PICKUPS.MAX_PICKUPS) {
// 8% chance to spawn health pickup
if (Math.random() < GameConfig.PICKUPS.HEALTH_PICKUP_CHANCE) {
this.spawnHealthPickup(x, y);
}

// 3% chance to spawn power-up
if (Math.random() < GameConfig.PICKUPS.POWERUP_PICKUP_CHANCE) {
this.spawnPowerUp(x, y);
}
}

this.soundManager.playEnemyKilled();
this.enemiesKilled++;

// Track achievements
const totalKills = this.metaProgression.getStats().totalKills + this.enemiesKilled;
if (this.enemiesKilled === 1) {
this.achievements.unlock('first_blood');
}
if (totalKills >= 100) {
this.achievements.unlock('mouse_hunter');
}
if (this.enemiesKilled >= 500) {
this.achievements.unlock('mouse_slayer');
}
if (totalKills >= 1000) {
this.achievements.unlock('exterminator');
}
if (isBoss) {
this.achievements.unlock('boss_killer');
}
});

// Listen for splitter enemy splitting
this.events.on('enemy-split', (x: number, y: number, target: any) => {
// Spawn 2 smaller/weaker enemies
for (let i = 0; i < 2; i++) {
const angle = (i / 2) * Math.PI * 2;
const offsetX = Math.cos(angle) * 30;
const offsetY = Math.sin(angle) * 30;

const newEnemy = new (Enemy as any)(this, x + offsetX, y + offsetY, target, 'fast');
this.enemySpawner.getEnemies().add(newEnemy);
}
});
}

private initGroups(): void {
// Create experience gems group
this.experienceGems = this.physics.add.group({
classType: ExperienceGem,
runChildUpdate: true
});

// Create health pickups group
this.healthPickups = this.physics.add.group({
classType: HealthPickup,
runChildUpdate: true
});

// Create power-ups group
this.powerUps = this.physics.add.group({
classType: PowerUp,
runChildUpdate: true
});
}

private initPhysics(): void {
// Setup collision between player and enemies
this.physics.add.overlap(
this.player,
this.enemySpawner.getEnemies(),
this.onPlayerHitEnemy as Phaser.Types.Physics.Arcade.ArcadePhysicsCallback,
undefined,
this
);
}

private initUI(): void {
// Create UI
this.createUI();
}

private initGameTimer(): void {
// Start game timer
this.time.addEvent({
delay: 1000,
callback: () => {
this.gameTime++;
this.updateUI();

// Check survival achievements
const minutes = Math.floor(this.gameTime / 60);
if (minutes >= 5) {
this.achievements.unlock('survivor');
}
if (minutes >= 10) {
this.achievements.unlock('endurance_cat');
}
if (minutes >= 15) {
this.achievements.unlock('unstoppable');
}

// Check close call achievement (less than 10% health)
if (this.player.health > 0 && this.player.health <= this.player.maxHealth * 0.1) {
this.achievements.unlock('close_call');
}

// Check player level achievements
if (this.player.level >= 10) {
this.achievements.unlock('learning_cat');
}
if (this.player.level >= 25) {
this.achievements.unlock('power_cat');
}
if (this.player.level >= 50) {
this.achievements.unlock('legendary_cat');
}
},
loop: true
});
}

private createBackground(): void {
// Base grass layer
const bg = this.add.rectangle(0, 0, GameConfig.WORLD.BOUNDS, GameConfig.WORLD.BOUNDS, 0x4A7C2E);
bg.setOrigin(0.5);
bg.setDepth(-10);

const graphics = this.add.graphics();
graphics.setDepth(-9);

// Create textured grass with multiple shades
for (let i = 0; i < 800; i++) {
const x = Phaser.Math.Between(-GameConfig.WORLD.BOUNDS / 2, GameConfig.WORLD.BOUNDS / 2);
const y = Phaser.Math.Between(-GameConfig.WORLD.BOUNDS / 2, GameConfig.WORLD.BOUNDS / 2);
const shade = Phaser.Math.Between(0, 3);
const colors = [0x5A8C3E, 0x4A7C2E, 0x3A6C1E, 0x4A7C2E];
const size = Phaser.Math.Between(20, 40);

graphics.fillStyle(colors[shade], 0.3);
graphics.fillCircle(x, y, size);
}

// Create winding dirt paths
const paths = [
{ startX: -GameConfig.WORLD.BOUNDS / 2, startY: 0, endX: GameConfig.WORLD.BOUNDS / 2, endY: 0 }, // Horizontal path
{ startX: 0, startY: -GameConfig.WORLD.BOUNDS / 2, endX: 0, endY: GameConfig.WORLD.BOUNDS / 2 }, // Vertical path
{ startX: -1500, startY: -1500, endX: 1500, endY: 1500 }, // Diagonal
{ startX: -1500, startY: 1500, endX: 1500, endY: -1500 }  // Other diagonal
];

paths.forEach(path => {
const pathSteps = 50;
for (let i = 0; i <= pathSteps; i++) {
const t = i / pathSteps;
const x = path.startX + (path.endX - path.startX) * t;
const y = path.startY + (path.endY - path.startY) * t;
const noise = Phaser.Math.Between(-30, 30);

graphics.fillStyle(0x8B7355, 0.6);
graphics.fillCircle(x + noise, y + noise, 35);
}
});

// Add flowers scattered around
for (let i = 0; i < 150; i++) {
const x = Phaser.Math.Between(-1900, 1900);
const y = Phaser.Math.Between(-1900, 1900);
const flowerColors = [0xFF69B4, 0xFFD700, 0xFF4500, 0x9370DB, 0xFFFFFF];
const color = flowerColors[Phaser.Math.Between(0, flowerColors.length - 1)];

// Flower petals
graphics.fillStyle(color, 0.8);
for (let j = 0; j < 5; j++) {
const angle = (j * Math.PI * 2) / 5;
const petalX = x + Math.cos(angle) * 3;
const petalY = y + Math.sin(angle) * 3;
graphics.fillCircle(petalX, petalY, 2);
}

// Flower center
graphics.fillStyle(0xFFD700, 1);
graphics.fillCircle(x, y, 1.5);
}

// Add decorative rocks
for (let i = 0; i < 60; i++) {
const x = Phaser.Math.Between(-1900, 1900);
const y = Phaser.Math.Between(-1900, 1900);
const size = Phaser.Math.Between(15, 35);
const grayShade = Phaser.Math.Between(0x606060, 0x808080);

graphics.fillStyle(grayShade, 0.9);
graphics.fillEllipse(x, y, size, size * 0.8);

// Rock highlight
graphics.fillStyle(0x909090, 0.5);
graphics.fillEllipse(x - size * 0.2, y - size * 0.2, size * 0.4, size * 0.3);
}

// Add darker grass clumps for depth
for (let i = 0; i < 100; i++) {
const x = Phaser.Math.Between(-1900, 1900);
const y = Phaser.Math.Between(-1900, 1900);

graphics.fillStyle(0x2A5C1E, 0.6);
for (let j = 0; j < 5; j++) {
const grassX = x + Phaser.Math.Between(-8, 8);
const grassY = y + Phaser.Math.Between(-8, 8);
graphics.fillEllipse(grassX, grassY, 3, 12);
}
}

// Add mushroom patches
for (let i = 0; i < 30; i++) {
const x = Phaser.Math.Between(-1900, 1900);
const y = Phaser.Math.Between(-1900, 1900);

// Mushroom cap
graphics.fillStyle(0xDC143C, 0.8);
graphics.fillCircle(x, y, 6);

// White spots
graphics.fillStyle(0xFFFFFF, 0.9);
graphics.fillCircle(x - 2, y - 1, 1.5);
graphics.fillCircle(x + 2, y + 1, 1);

// Stem
graphics.fillStyle(0xF5F5DC, 0.9);
graphics.fillRect(x - 2, y, 4, 5);
}

// Generate texture once for performance
graphics.generateTexture('ground-texture', GameConfig.WORLD.BOUNDS, GameConfig.WORLD.BOUNDS);
graphics.destroy();

// Use the generated texture
const groundSprite = this.add.image(0, 0, 'ground-texture');
groundSprite.setDepth(-9);
groundSprite.setOrigin(0.5);
}

private createHardwoodFloor(graphics: Phaser.GameObjects.Graphics): void {
const plankWidth = 200;
const plankHeight = 32;

for (let y = -GameConfig.WORLD.BOUNDS / 2; y < GameConfig.WORLD.BOUNDS / 2; y += plankHeight) {
let x = -GameConfig.WORLD.BOUNDS / 2;
const rowOffset = (y / plankHeight % 2) * (plankWidth / 2);

while (x < GameConfig.WORLD.BOUNDS / 2) {
const plankX = x + rowOffset;
const woodShade = Phaser.Math.Between(0, 2);
let color;
if (woodShade === 0) color = 0x8B6914;
else if (woodShade === 1) color = 0x9B7714;
else color = 0x7B5914;

graphics.fillStyle(color, 1);
graphics.fillRect(plankX, y, plankWidth - 2, plankHeight - 1);

// Wood grain texture
graphics.fillStyle(0x6B4904, 0.15);
for (let i = 0; i < 3; i++) {
const grainY = y + Phaser.Math.Between(2, plankHeight - 4);
graphics.fillRect(plankX + 5, grainY, plankWidth - 12, 1);
}

// Plank border
graphics.lineStyle(1, 0x5B3904, 0.4);
graphics.strokeRect(plankX, y, plankWidth - 2, plankHeight - 1);

x += plankWidth;
}
}
}

private createLargeRugs(graphics: Phaser.GameObjects.Graphics): void {
// Large central rug
this.createDetailedRug(graphics, -400, -300, 600, 400, 0xB22222, 0x8B1A1A);

// Side rugs
this.createDetailedRug(graphics, -1200, 600, 400, 300, 0x4682B4, 0x315F8C);
this.createDetailedRug(graphics, 800, -800, 450, 350, 0x228B22, 0x1A6B1A);
this.createDetailedRug(graphics, 600, 700, 350, 250, 0x9370DB, 0x7050AB);
}

private createDetailedRug(graphics: Phaser.GameObjects.Graphics, x: number, y: number, width: number, height: number, baseColor: number, darkColor: number): void {
// Rug base
graphics.fillStyle(baseColor, 0.7);
graphics.fillRect(x, y, width, height);

// Rug border (multiple layers)
graphics.lineStyle(12, darkColor, 0.8);
graphics.strokeRect(x + 6, y + 6, width - 12, height - 12);

graphics.lineStyle(6, baseColor, 0.9);
graphics.strokeRect(x + 12, y + 12, width - 24, height - 24);

// Pattern in center
const centerX = x + width / 2;
const centerY = y + height / 2;

// Diamond pattern
graphics.fillStyle(darkColor, 0.5);
for (let i = 0; i < 3; i++) {
for (let j = 0; j < 3; j++) {
const dx = x + 50 + i * (width - 100) / 2;
const dy = y + 50 + j * (height - 100) / 2;
graphics.fillCircle(dx, dy, 15);
}
}

// Fringe on edges
graphics.fillStyle(darkColor, 0.6);
for (let i = 0; i < width; i += 8) {
graphics.fillRect(x + i, y - 4, 4, 8);
graphics.fillRect(x + i, y + height - 4, 4, 8);
}
for (let i = 0; i < height; i += 8) {
graphics.fillRect(x - 4, y + i, 8, 4);
graphics.fillRect(x + width - 4, y + i, 8, 4);
}
}

private createTileSections(graphics: Phaser.GameObjects.Graphics): void {
// Kitchen-like tile area
this.createTileArea(graphics, -1600, -1600, 500, 500, 0xF5F5DC);

// Bathroom-like tile area
this.createTileArea(graphics, 1200, 1200, 450, 450, 0xE0FFFF);
}

private createTileArea(graphics: Phaser.GameObjects.Graphics, startX: number, startY: number, width: number, height: number, tileColor: number): void {
const tileSize = 32;

for (let x = startX; x < startX + width; x += tileSize) {
for (let y = startY; y < startY + height; y += tileSize) {
const isOffset = ((x - startX) / tileSize + (y - startY) / tileSize) % 2;
const colorObj = Phaser.Display.Color.IntegerToColor(tileColor);
const darkerColor = Phaser.Display.Color.GetColor(
colorObj.red - 15,
colorObj.green - 15,
colorObj.blue - 15
);

const color = isOffset ? tileColor : darkerColor;
graphics.fillStyle(color, 1);
graphics.fillRect(x, y, tileSize - 2, tileSize - 2);

// Grout lines
graphics.lineStyle(2, 0xC0C0C0, 0.6);
graphics.strokeRect(x, y, tileSize - 2, tileSize - 2);
}
}
}

private addAmbientDetails(): void {
// Add sunlight patches (warm spots where cats like to sleep)
const sunPatches = [
{ x: -600, y: -700, radius: 120, color: 0xFFE4B5 },
{ x: 400, y: 300, radius: 150, color: 0xFFE4B5 },
{ x: 1000, y: -400, radius: 100, color: 0xFFE4B5 },
{ x: -1100, y: 800, radius: 130, color: 0xFFE4B5 }
];

sunPatches.forEach(patch => {
const sunlight = this.add.graphics();
sunlight.fillStyle(patch.color, 0.15);
sunlight.fillCircle(patch.x, patch.y, patch.radius);
sunlight.setDepth(-2);
});

// Add wall shadows (darker areas near "walls")
const shadows = [
{ x: -1900, y: 0, width: 100, height: 4000, horizontal: false },
{ x: 0, y: -1900, width: 4000, height: 100, horizontal: true },
{ x: 1800, y: 0, width: 100, height: 4000, horizontal: false },
{ x: 0, y: 1800, width: 4000, height: 100, horizontal: true }
];

shadows.forEach(shadow => {
const gradient = this.add.graphics();
if (shadow.horizontal) {
gradient.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.2, 0.2, 0, 0);
} else {
gradient.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.2, 0, 0.2, 0);
}
gradient.fillRect(shadow.x, shadow.y, shadow.width, shadow.height);
gradient.setDepth(-2);
});
}

private createRugPattern(graphics: Phaser.GameObjects.Graphics, x: number, y: number, color: number): void {
// Draw decorative rug
graphics.fillStyle(color, 0.4);
graphics.fillRect(x, y, 200, 150);

// Add border pattern
graphics.lineStyle(4, color, 0.6);
graphics.strokeRect(x + 5, y + 5, 190, 140);

// Add inner decorative pattern
graphics.fillStyle(color, 0.3);
for (let i = 0; i < 3; i++) {
for (let j = 0; j < 3; j++) {
graphics.fillCircle(x + 50 + i * 50, y + 40 + j * 35, 8);
}
}
}

private createPawPrintTrail(graphics: Phaser.GameObjects.Graphics, startX: number, startY: number, endX: number, endY: number): void {
const steps = 15;
const deltaX = (endX - startX) / steps;
const deltaY = (endY - startY) / steps;

graphics.fillStyle(0x4d3827, 0.15);
for (let i = 0; i < steps; i++) {
const x = startX + deltaX * i;
const y = startY + deltaY * i;
const side = i % 2 === 0 ? 1 : -1;
const offsetX = side * 10;

// Draw paw print
graphics.fillCircle(x + offsetX, y, 4);
graphics.fillCircle(x + offsetX - 3, y - 4, 2);
graphics.fillCircle(x + offsetX + 3, y - 4, 2);
graphics.fillCircle(x + offsetX - 2, y - 6, 2);
graphics.fillCircle(x + offsetX + 2, y - 6, 2);
}
}

private addCatDecorations(): void {
// Add scattered yarn balls
const yarnPositions = [
[-1200, -400], [800, -900], [-600, 600], [1100, 300], [-200, -700],
[400, 800], [-900, 100], [600, -200], [-1400, 800], [1300, -500]
];

yarnPositions.forEach(([x, y]) => {
this.createYarnBallDecoration(x, y);
});

// Add food/water bowls
const bowlPositions = [
[-1000, 500], [700, 700], [-500, -600], [1000, -300]
];

bowlPositions.forEach(([x, y]) => {
this.createFoodBowl(x, y);
});

// Add cat toys (mice)
const toyPositions = [
[-800, -200], [500, 500], [-300, 900], [900, -800], [200, -400]
];

toyPositions.forEach(([x, y]) => {
this.createToyMouse(x, y);
});

// Add scratching posts
const scratchingPostPositions = [
[-700, -800], [1100, 600], [-1300, 200]
];

scratchingPostPositions.forEach(([x, y]) => {
this.createScratchingPost(x, y);
});

// Add cat beds
const bedPositions = [
[-1100, -300], [800, 200], [-400, 700], [1200, -700]
];

bedPositions.forEach(([x, y]) => {
this.createCatBed(x, y);
});

// Add cat trees
const catTreePositions = [
[-1400, -600], [1200, 800], [600, -600]
];

catTreePositions.forEach(([x, y]) => {
this.createCatTree(x, y);
});

// Add cardboard boxes
const boxPositions = [
[-500, -900], [1000, 100], [-1200, 700], [400, -300], [-200, 400]
];

boxPositions.forEach(([x, y]) => {
this.createCardboardBox(x, y);
});

// Add house plants
const plantPositions = [
[-900, -500], [800, 900], [-100, -800], [1300, 400]
];

plantPositions.forEach(([x, y]) => {
this.createHousePlant(x, y);
});

// Add furniture (couches)
const furniturePositions = [
[-1300, -200], [900, 500]
];

furniturePositions.forEach(([x, y]) => {
this.createCouch(x, y);
});
}

private createYarnBallDecoration(x: number, y: number): void {
const graphics = this.add.graphics();
const colors = [0xFF69B4, 0x87CEEB, 0x98FB98, 0xFFB6C1, 0xDDA0DD];
const color = Phaser.Utils.Array.GetRandom(colors);

// Yarn ball
graphics.fillStyle(color, 0.5);
graphics.fillCircle(x, y, 8);

// Yarn texture
graphics.lineStyle(1, color, 0.7);
graphics.arc(x, y, 6, 0, Math.PI);
graphics.arc(x, y, 6, Math.PI / 2, Math.PI * 1.5);

graphics.setDepth(-1);
}

private createFoodBowl(x: number, y: number): void {
const graphics = this.add.graphics();

// Bowl
graphics.fillStyle(0xC0C0C0, 0.6);
graphics.fillEllipse(x, y, 24, 16);

// Inner shadow
graphics.fillStyle(0x808080, 0.4);
graphics.fillEllipse(x, y, 18, 12);

// Food/water
graphics.fillStyle(0x8B4513, 0.5);
graphics.fillEllipse(x, y, 14, 9);

graphics.setDepth(-1);
}

private createToyMouse(x: number, y: number): void {
const graphics = this.add.graphics();

// Mouse body
graphics.fillStyle(0x808080, 0.4);
graphics.fillEllipse(x, y, 10, 6);

// Ears
graphics.fillCircle(x - 3, y - 3, 2);
graphics.fillCircle(x - 1, y - 3, 2);

// Tail
graphics.lineStyle(1, 0x808080, 0.4);
graphics.lineBetween(x + 5, y, x + 10, y - 5);

graphics.setDepth(-1);
}

private createScratchingPost(x: number, y: number): void {
const graphics = this.add.graphics();

// Base
graphics.fillStyle(0x654321, 0.5);
graphics.fillEllipse(x, y + 30, 30, 15);

// Post
graphics.fillStyle(0x8B7355, 0.6);
graphics.fillRect(x - 8, y - 20, 16, 50);

// Rope texture
graphics.lineStyle(1, 0x6B5335, 0.5);
for (let i = 0; i < 10; i++) {
graphics.lineBetween(x - 8, y - 20 + i * 5, x + 8, y - 20 + i * 5);
}

// Top platform
graphics.fillStyle(0x654321, 0.5);
graphics.fillRect(x - 12, y - 25, 24, 5);

graphics.setDepth(-1);
}

private createCatBed(x: number, y: number): void {
const graphics = this.add.graphics();

// Bed base
graphics.fillStyle(0x8B7355, 0.5);
graphics.fillEllipse(x, y, 40, 30);

// Inner cushion
graphics.fillStyle(0xFFB6C1, 0.4);
graphics.fillEllipse(x, y, 32, 24);

// Texture
graphics.fillStyle(0x8B7355, 0.3);
graphics.fillEllipse(x - 8, y - 5, 8, 6);
graphics.fillEllipse(x + 6, y + 4, 8, 6);

graphics.setDepth(-1);
}

private createCatTree(x: number, y: number): void {
const graphics = this.add.graphics();

// Base platform
graphics.fillStyle(0x654321, 0.6);
graphics.fillEllipse(x, y + 50, 60, 30);

// Main post
graphics.fillStyle(0x8B7355, 0.7);
graphics.fillRect(x - 12, y - 50, 24, 100);

// Rope texture on post
graphics.lineStyle(2, 0x6B5335, 0.6);
for (let i = 0; i < 20; i++) {
graphics.lineBetween(x - 12, y - 50 + i * 5, x + 12, y - 50 + i * 5);
}

// Middle platform
graphics.fillStyle(0x654321, 0.6);
graphics.fillRect(x - 25, y, 50, 8);

// Top platform
graphics.fillStyle(0x654321, 0.6);
graphics.fillEllipse(x, y - 55, 40, 20);

// Hanging toy
graphics.lineStyle(1, 0x888888, 0.5);
graphics.lineBetween(x + 15, y, x + 15, y + 15);
graphics.fillStyle(0xFF69B4, 0.6);
graphics.fillCircle(x + 15, y + 18, 4);

graphics.setDepth(-1);
}

private createCardboardBox(x: number, y: number): void {
const graphics = this.add.graphics();

// Box body
graphics.fillStyle(0xD2B48C, 0.6);
graphics.fillRect(x - 20, y - 15, 40, 30);

// Box shading
graphics.fillStyle(0xC19A6B, 0.4);
graphics.fillRect(x - 18, y - 13, 36, 26);

// Box flaps
graphics.fillStyle(0xD2B48C, 0.5);
graphics.fillTriangle(
x - 20, y - 15,
x, y - 25,
x + 20, y - 15
);

// Tape lines
graphics.lineStyle(2, 0xA0826D, 0.5);
graphics.lineBetween(x - 20, y, x + 20, y);
graphics.lineBetween(x, y - 15, x, y + 15);

// Box opening (if cat is hiding inside)
graphics.fillStyle(0x000000, 0.3);
graphics.fillCircle(x + 10, y - 5, 5);

graphics.setDepth(-1);
}

private createHousePlant(x: number, y: number): void {
const graphics = this.add.graphics();

// Pot
graphics.fillStyle(0x8B4513, 0.6);
graphics.fillRect(x - 12, y + 10, 24, 20);
graphics.fillStyle(0xA0522D, 0.5);
graphics.fillRect(x - 10, y + 12, 20, 16);

// Soil
graphics.fillStyle(0x654321, 0.5);
graphics.fillRect(x - 10, y + 10, 20, 5);

// Plant stem
graphics.lineStyle(3, 0x228B22, 0.7);
graphics.lineBetween(x, y + 10, x - 5, y - 20);
graphics.lineBetween(x, y + 10, x + 3, y - 25);
graphics.lineBetween(x, y + 10, x + 8, y - 15);

// Leaves
graphics.fillStyle(0x32CD32, 0.6);
graphics.fillEllipse(x - 8, y - 22, 12, 8);
graphics.fillEllipse(x + 5, y - 27, 10, 7);
graphics.fillEllipse(x + 10, y - 17, 11, 8);
graphics.fillEllipse(x - 3, y - 12, 9, 6);

// Leaf highlights
graphics.fillStyle(0x90EE90, 0.4);
graphics.fillEllipse(x - 6, y - 22, 6, 4);
graphics.fillEllipse(x + 6, y - 27, 5, 3);

graphics.setDepth(-1);
}

private createCouch(x: number, y: number): void {
const graphics = this.add.graphics();

// Couch base/seat
graphics.fillStyle(0x8B7355, 0.6);
graphics.fillRect(x - 50, y, 100, 30);

// Couch back
graphics.fillStyle(0x9D8565, 0.6);
graphics.fillRect(x - 50, y - 25, 100, 25);

// Armrests
graphics.fillStyle(0x8B7355, 0.7);
graphics.fillRect(x - 55, y - 15, 10, 40);
graphics.fillRect(x + 45, y - 15, 10, 40);

// Cushion details
graphics.lineStyle(2, 0x7B6345, 0.5);
graphics.lineBetween(x - 20, y, x - 20, y + 30);
graphics.lineBetween(x + 20, y, x + 20, y + 30);

// Highlights
graphics.fillStyle(0xAD9575, 0.3);
graphics.fillRect(x - 45, y - 20, 90, 8);
graphics.fillRect(x - 45, y + 5, 90, 8);

graphics.setDepth(-1);
}

private createUI(): void {
// Create health bar background
const healthBarBg = this.add.rectangle(20, 20, 204, 24, 0x000000, 0.7);
healthBarBg.setOrigin(0, 0);
healthBarBg.setScrollFactor(0);
healthBarBg.setDepth(1000);

// Create health bar
const healthBar = this.add.rectangle(22, 22, 200, 20, 0xff0000);
healthBar.setOrigin(0, 0);
healthBar.setScrollFactor(0);
healthBar.setDepth(1001);
(this as any).healthBar = healthBar;

// Health text
this.healthText = this.add.text(230, 25, '', {
fontSize: '18px',
color: '#ffffff',
fontStyle: 'bold'
});
this.healthText.setScrollFactor(0);
this.healthText.setDepth(1002);

// Create XP bar background
const xpBarBg = this.add.rectangle(20, 50, 304, 16, 0x000000, 0.7);
xpBarBg.setOrigin(0, 0);
xpBarBg.setScrollFactor(0);
xpBarBg.setDepth(1000);

// Create XP bar
const xpBar = this.add.rectangle(22, 52, 300, 12, 0x00ff00);
xpBar.setOrigin(0, 0);
xpBar.setScrollFactor(0);
xpBar.setDepth(1001);
(this as any).xpBar = xpBar;

// Level text
this.levelText = this.add.text(330, 53, '', {
fontSize: '16px',
color: '#00ff00',
fontStyle: 'bold'
});
this.levelText.setScrollFactor(0);
this.levelText.setDepth(1002);

// Time survived (top right)
this.timeText = this.add.text(GameConfig.WIDTH - 20, 20, '', {
fontSize: '22px',
color: '#ffffff',
fontStyle: 'bold'
});
this.timeText.setOrigin(1, 0);
this.timeText.setScrollFactor(0);
this.timeText.setDepth(1002);

// Instructions
const instructions = this.add.text(
GameConfig.WIDTH / 2,
GameConfig.HEIGHT - 32,
'WASD or Arrow Keys to Move | Auto-Attack',
{
fontSize: '16px',
color: '#888888'
}
);
instructions.setOrigin(0.5);
instructions.setScrollFactor(0);

this.updateUI();
}

private updateUI(): void {
const minutes = Math.floor(this.gameTime / 60);
const seconds = this.gameTime % 60;
this.timeText.setText(`⏱️ ${minutes}:${seconds.toString().padStart(2, '0')}`);

// Update health bar
const healthPercent = Math.max(0, this.player.health / this.player.maxHealth);
const healthBar = (this as any).healthBar;
if (healthBar) {
healthBar.width = 200 * healthPercent;

// Color based on health percentage
if (healthPercent > 0.5) {
healthBar.setFillStyle(0x00ff00); // Green
} else if (healthPercent > 0.25) {
healthBar.setFillStyle(0xffff00); // Yellow
} else {
healthBar.setFillStyle(0xff0000); // Red
}
}
this.healthText.setText(`${Math.floor(this.player.health)}/${this.player.maxHealth}`);

// Update XP bar
const xpPercent = this.player.experience / this.player.getXPForNextLevel();
const xpBar = (this as any).xpBar;
if (xpBar) {
xpBar.width = 300 * xpPercent;
}
this.levelText.setText(`Lv.${this.player.level}`);
}

private spawnExperienceGem(x: number, y: number): void {
// Limit total gems for performance
if (this.experienceGems.getChildren().length >= GameConfig.PICKUPS.MAX_GEMS) {
// Remove oldest gem
const oldest = this.experienceGems.getChildren()[0];
if (oldest) oldest.destroy();
}

// Apply XP gain multiplier from meta upgrades
const xpValue = Math.floor(GameConfig.PICKUPS.EXP_GEM_VALUE * this.xpGainMultiplier);

const gem = new ExperienceGem(this, x, y, this.player, xpValue);
// Set the base magnet range from meta upgrades
gem.setMagnetRange(this.baseMagnetRange);
this.experienceGems.add(gem);
}

private spawnHealthPickup(x: number, y: number): void {
const health = new HealthPickup(this, x, y, this.player, GameConfig.PICKUPS.HEALTH_PICKUP_VALUE);
// Set the base magnet range from meta upgrades
health.setMagnetRange(this.baseMagnetRange);
this.healthPickups.add(health);
}

private spawnPowerUp(x: number, y: number): void {
const types: PowerUpType[] = ['speed', 'invincibility', 'damage', 'magnet', 'multi_shot', 'time_slow'];
const randomType = Phaser.Utils.Array.GetRandom(types);

// Determine rarity - common 70%, rare 25%, epic 5%
const rarityRoll = Math.random() * 100;
let rarity: any = 'common';
if (rarityRoll < 5) {
rarity = 'epic';
} else if (rarityRoll < 30) {
rarity = 'rare';
}

const powerUp = new PowerUp(this, x, y, this.player, randomType, rarity);
// Set the base magnet range from meta upgrades
powerUp.setMagnetRange(this.baseMagnetRange);
this.powerUps.add(powerUp);
}

private handleLevelUp(): void {
// Generate 3 random upgrades
const upgrades = this.upgradeSystem.generateRandomUpgrades(3);

// Show upgrade UI
this.upgradeUI.show(upgrades, (selectedUpgrade) => {
this.upgradeSystem.applyUpgrade(selectedUpgrade);
this.updateUI();
});
}

public enableYarnBallWeapon(): void {
if (!this.yarnBallWeapon) {
this.yarnBallWeapon = new YarnBallWeapon(this, this.player, this.enemySpawner.getEnemies());
this.yarnBallWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('yarnball');
}
}

public enableLaserPointerWeapon(): void {
if (!this.laserPointerWeapon) {
this.laserPointerWeapon = new LaserPointerWeapon(this, this.player, this.enemySpawner.getEnemies());
this.laserPointerWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('laser');
}
}

public enableFishWeapon(): void {
if (!this.fishWeapon) {
this.fishWeapon = new FishWeapon(this, this.player, this.enemySpawner.getEnemies());
this.fishWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('fish');
}
}

public enableCatnipBombWeapon(): void {
if (!this.catnipBombWeapon) {
this.catnipBombWeapon = new CatnipBombWeapon(this, this.player, this.enemySpawner.getEnemies());
this.catnipBombWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('catnip');
}
}

public enableCatCollarWeapon(): void {
if (!this.catCollarWeapon) {
this.catCollarWeapon = new CatCollarWeapon(this, this.player, this.enemySpawner.getEnemies());
this.catCollarWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('collar');
}
}

public enableMilkSplashWeapon(): void {
if (!this.milkSplashWeapon) {
this.milkSplashWeapon = new MilkSplashWeapon(this, this.player, this.enemySpawner.getEnemies());
this.milkSplashWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('milk');
}
}

public enableScratchingPostWeapon(): void {
if (!this.scratchingPostWeapon) {
this.scratchingPostWeapon = new ScratchingPostWeapon(this, this.player, this.enemySpawner.getEnemies());
this.scratchingPostWeapon.updateDamageMultiplier(this.baseDamageMultiplier);
this.trackWeaponEquipped('post');
}
}

private trackWeaponEquipped(weaponId: string): void {
this.weaponsEquipped.add(weaponId);

// Check arsenal achievement (4 different weapons)
if (this.weaponsEquipped.size >= 4) {
this.achievements.unlock('arsenal');
}
}

private applyMetaUpgrades(): void {
// Apply max health upgrade
const maxHealthValue = this.metaProgression.getUpgradeValue('max_health');
if (maxHealthValue > 0) {
this.player.maxHealth = maxHealthValue;
this.player.health = maxHealthValue;
}

// Apply move speed upgrade
const moveSpeedValue = this.metaProgression.getUpgradeValue('move_speed');
if (moveSpeedValue > 0) {
this.player.setData('moveSpeed', moveSpeedValue);
}

// Apply damage upgrade (multiplier)
const damageValue = this.metaProgression.getUpgradeValue('damage');
if (damageValue > 1) {
this.baseDamageMultiplier = damageValue;
}

// Apply XP gain upgrade (multiplier)
const xpGainValue = this.metaProgression.getUpgradeValue('xp_gain');
if (xpGainValue > 1) {
this.xpGainMultiplier = xpGainValue;
}

// Apply pickup range upgrade
const pickupRangeValue = this.metaProgression.getUpgradeValue('pickup_range');
if (pickupRangeValue > 0) {
this.baseMagnetRange = pickupRangeValue;
}

// Apply cooldown reduction upgrade
const cooldownValue = this.metaProgression.getUpgradeValue('cooldown_reduction');
if (cooldownValue > 0 && cooldownValue < 1) {
this.cooldownMultiplier = cooldownValue;
}

// Apply health regen upgrade
const healthRegenValue = this.metaProgression.getUpgradeValue('health_regen');
if (healthRegenValue > 0) {
// Add health regeneration timer
this.time.addEvent({
delay: 1000, // Every second
callback: () => {
if (this.player.health < this.player.maxHealth) {
this.player.health = Math.min(this.player.maxHealth, this.player.health + healthRegenValue);
this.updateUI();
}
},
loop: true
});
}
}

public getBaseDamageMultiplier(): number {
return this.baseDamageMultiplier;
}

public getCooldownMultiplier(): number {
return this.cooldownMultiplier;
}

public getBaseMagnetRange(): number {
return this.baseMagnetRange;
}

public showGameOver(): void {
this.soundManager.playGameOver();

// Check run-based achievements before transitioning
const stats = this.metaProgression.getStats();
if (stats.totalRuns >= 10) {
this.achievements.unlock('persistent');
}
if (stats.totalCatTreatsEarned >= 500) {
this.achievements.unlock('treat_hoarder');
}

this.scene.start('GameOverScene', {
survivalTime: this.gameTime,
level: this.player.level,
enemiesKilled: this.enemiesKilled
});
}

private onPlayerHitEnemy(
playerObj: Phaser.GameObjects.GameObject,
enemyObj: Phaser.GameObjects.GameObject
): void {
const player = playerObj as Player;

// Damage player over time (simple collision damage)
if (!player.getData('lastDamageTime') || this.time.now - player.getData('lastDamageTime') > GameConfig.PLAYER.DAMAGE_COOLDOWN) {
player.takeDamage(GameConfig.PLAYER.COLLISION_DAMAGE);
player.setData('lastDamageTime', this.time.now);
this.soundManager.playPlayerHit();
this.updateUI();
}
}

update(): void {
this.player.update();
if (this.powerUpDisplay) {
this.powerUpDisplay.update();
}
}
}
